/**
 * Brief Promotion Service
 *
 * Connects the Decision Log (formation) to the Plan of Record (execution).
 *
 * When a brief is decided, the decision-maker can promote it to the Plan of Record.
 * This creates a Decision record in the main app (with full rationale infrastructure)
 * and links it back to the brief via promoted_decision_id.
 *
 * Promotion types (what the decision does to the Plan of Record):
 *   creates  — establishes a net-new commitment
 *   adds     — documents a decision against an existing planned initiative
 *   changes  — modifies a prior commitment (the prior Decision is marked superseded)
 *   reverses — cancels or stops a prior commitment
 */

import { useDecisionsStore } from '../../stores/prioritization/decisionsStore';
import { promoteBrief } from './briefService';
import type { Brief, BriefDecision, BriefOption, PromotionType } from '../../types/brief';
import type { CreateDecisionPayload, DecisionImpact } from '../../types/prioritization/decisions';
import { DECISION_TYPE_LABELS, DECISION_SCOPE_LABELS } from '../../types/brief';

export interface PromoteToRecordOptions {
  brief: Brief;
  briefDecision: BriefDecision;
  selectedOptions: BriefOption[];
  rejectedOptions: BriefOption[];
  promotionType: PromotionType;
  /** For 'changes' or 'reverses': describe what existing commitment is affected. */
  promotionNote: string;
  impact: DecisionImpact;
}

export interface PromotionResult {
  decisionId: string;
  decisionTitle: string;
}

/**
 * Build the body text for the Decision record — a structured markdown-style
 * summary of the brief that will live permanently in the main app's decision feed.
 */
function buildDecisionBody(opts: PromoteToRecordOptions): string {
  const { brief, briefDecision, selectedOptions, rejectedOptions } = opts;
  const lines: string[] = [];

  lines.push(`Decision authority: ${brief.decision_authority ?? 'Not recorded'}`);
  if (brief.decision_type) {
    lines.push(`Decision type: ${DECISION_TYPE_LABELS[brief.decision_type]}`);
  }
  if (brief.decision_scope) {
    const scopeLabel = DECISION_SCOPE_LABELS[brief.decision_scope];
    lines.push(`Scope: ${scopeLabel}${brief.scope_label ? ` — ${brief.scope_label}` : ''}`);
  }
  if (brief.deadline) {
    lines.push(`Decided by: ${new Date(brief.deadline + 'T00:00:00').toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}`);
  }

  if (selectedOptions.length > 0) {
    lines.push('');
    lines.push('Selected:');
    selectedOptions.forEach((o) => lines.push(`  • ${o.name}`));
  }

  if (rejectedOptions.length > 0) {
    lines.push('');
    lines.push('Not selected:');
    rejectedOptions.forEach((o) => lines.push(`  • ${o.name}`));
  }

  if (briefDecision.conditions) {
    lines.push('');
    lines.push(`Signal for change: ${briefDecision.conditions}`);
  }

  lines.push('');
  lines.push(`Source: Decision Log brief — ${brief.id}`);

  return lines.join('\n');
}

/**
 * Build structured rationale from the brief and its decision record.
 * Maps brief fields → Decision.rationale* fields for full provenance in the main app.
 */
function buildRationale(opts: PromoteToRecordOptions): {
  rationaleContext: string;
  rationaleReasoning: string;
  rationaleAlternatives: string;
  reconsiderationConditions: string;
} {
  const { brief, briefDecision, selectedOptions, rejectedOptions } = opts;

  // Context: what was framed going into this decision
  const rationaleContext = [
    brief.context ?? '',
    opts.promotionNote ? `\n\nPrior commitment affected: ${opts.promotionNote}` : '',
  ].filter(Boolean).join('').trim();

  // Reasoning: why the selected option(s) were chosen
  const selectedNames = selectedOptions.map((o) => o.name).join(', ');
  const rationaleReasoning = [
    briefDecision.selected_rationale ?? '',
    selectedNames ? `\n\nSelected: ${selectedNames}` : '',
  ].filter(Boolean).join('').trim();

  // Alternatives: what was considered and not chosen
  const rejectedNames = rejectedOptions.map((o) => o.name).join(', ');
  const rationaleAlternatives = [
    briefDecision.rejected_rationale ?? '',
    rejectedNames ? `\n\nOptions not selected: ${rejectedNames}` : '',
  ].filter(Boolean).join('').trim();

  return {
    rationaleContext,
    rationaleReasoning,
    rationaleAlternatives,
    reconsiderationConditions: briefDecision.conditions ?? '',
  };
}

/**
 * Promote a decided brief to the Plan of Record.
 *
 * 1. Creates a Decision in the main app's decisions table (via decisionsStore).
 * 2. Writes promoted_decision_id back to the brief.
 * 3. Returns the created decision's ID and title.
 */
export async function promoteBriefToRecord(opts: PromoteToRecordOptions): Promise<PromotionResult> {
  const { brief, promotionType, impact, briefDecision } = opts;
  const rationale = buildRationale(opts);
  const body = buildDecisionBody(opts);

  const payload: CreateDecisionPayload = {
    title: brief.title,
    body,
    decisionKind: 'manual',
    decisionClass: 'selection',
    impact,
    tags: brief.tags ?? [],
    rationaleContext:          rationale.rationaleContext  || undefined,
    rationaleReasoning:        rationale.rationaleReasoning || undefined,
    rationaleAlternatives:     rationale.rationaleAlternatives || undefined,
    reconsiderationConditions: rationale.reconsiderationConditions || undefined,
    reviewByDate: brief.deadline ?? undefined,
    // 'decision_brief' origin module marks these as promoted from the Decision Log
    originModule: 'decision_brief',
    // Source field stores the brief ID for cross-surface traceability
    source: `brief:${brief.id}`,
    decidedAt: briefDecision.decided_at ?? new Date().toISOString(),
  };

  const decision = await useDecisionsStore.getState().createDecision(payload);
  if (!decision) {
    throw new Error('Failed to create Plan of Record entry — decisions store returned null');
  }

  // Write the link back to the brief through its governed RPC (migration 794;
  // idempotent, so a retry after a failed stamp cannot promote twice).
  await promoteBrief(brief.id, decision.id, promotionType, opts.promotionNote || null);

  return { decisionId: decision.id, decisionTitle: decision.title };
}
